import React, { ReactNode } from 'react';
import { Card } from 'antd';
import clsx from 'clsx';

export type AnalyticsChartCardVariant = 'standard' | 'flush';

export type AnalyticsChartCardProps = {
	title: ReactNode;
	extra?: ReactNode;
	children: ReactNode;
	/**
	 * 'standard' applies the body padding and break-inside-avoid print classes used by the
	 * metric line and spend bar charts. 'flush' uses Card defaults (no body padding override)
	 * with the simpler print classes used by the WO Performance card.
	 */
	variant?: AnalyticsChartCardVariant;
	className?: string;
};

/**
 * Marker class wired up in src/styles/tailwind.css — its sole job is to override the global
 * `.ant-card-head-title { text-transform: uppercase !important }` rule in src/index.less so
 * analytics chart card headers render in sentence case like the lifecycle reference.
 */
const ANALYTICS_CHART_CARD_MARKER_CLASS = 'analytics-chart-card';

const VARIANT_CLASS_NAMES: Record<AnalyticsChartCardVariant, string> = {
	standard:
		ANALYTICS_CHART_CARD_MARKER_CLASS +
		' print:!h-auto print:!min-h-0 print:!break-inside-avoid ' +
		'print:[&_.ant-card-body]:!break-inside-avoid ' +
		'print:[&_.ant-card-body]:!px-2 ' +
		'print:[&_.ant-card-body]:!pb-3 ' +
		'print:[&_.ant-card-body]:!pt-4 ' +
		'[&_.ant-card-body]:pb-6 [&_.ant-card-body]:pl-4 [&_.ant-card-body]:pr-9 [&_.ant-card-body]:pt-[57px]',
	flush: ANALYTICS_CHART_CARD_MARKER_CLASS + ' print:!h-auto print:!min-h-0',
};

const AnalyticsChartCard = ({
	title,
	extra,
	children,
	variant = 'standard',
	className,
}: AnalyticsChartCardProps) => {
	// Always include the marker + variant classes so callers that pass `className` (e.g.
	// `mt-2` on the breakdown chart card) augment them rather than replace them — replacing
	// the variant string was dropping the `analytics-chart-card` marker that beats the global
	// uppercase rule, and dropping the body-padding utilities the standard variant needs.
	const resolvedClassName = clsx(VARIANT_CLASS_NAMES[variant], className);
	return (
		<Card className={resolvedClassName} title={title} extra={extra}>
			{children}
		</Card>
	);
};

export default AnalyticsChartCard;
